'use client'

import { useMemo, useState } from 'react'
import { ToolField, ToolResultCard, ToolResultRow, ToolSelect, toolCurrency, toolNum } from './tool-inputs'

type FenceType = 'Palisade' | 'Diamond Mesh' | 'Timber' | 'Clearvu'

// Same spec table as the in-app fencing calculator.
const FENCE_SPECS: Record<FenceType, { postSpacingM: number; concretePerPost: number }> = {
  Palisade: { postSpacingM: 2.5, concretePerPost: 0.025 },
  'Diamond Mesh': { postSpacingM: 3.0, concretePerPost: 0.020 },
  Timber: { postSpacingM: 2.4, concretePerPost: 0.030 },
  Clearvu: { postSpacingM: 2.5, concretePerPost: 0.025 },
}
const CONCRETE_BAG_VOL = 0.025

export function FencingCalculatorTool() {
  const [length, setLength] = useState('30')
  const [fenceType, setFenceType] = useState<FenceType>('Palisade')
  const [pricePerM, setPricePerM] = useState('680')
  const [pricePerPost, setPricePerPost] = useState('180')
  const [concreteBagPrice, setConcreteBagPrice] = useState('75')

  const r = useMemo(() => {
    const spec = FENCE_SPECS[fenceType]
    const L = toolNum(length)
    const posts = Math.ceil(L / spec.postSpacingM) + 1
    const panels = Math.ceil(L / spec.postSpacingM)
    const concreteBags = Math.ceil((posts * spec.concretePerPost) / CONCRETE_BAG_VOL)
    const cost = L * toolNum(pricePerM) + posts * toolNum(pricePerPost) + concreteBags * toolNum(concreteBagPrice)
    return { posts, panels, concreteBags, cost }
  }, [length, fenceType, pricePerM, pricePerPost, concreteBagPrice])

  return (
    <div className="grid gap-6 md:grid-cols-[1fr_380px] md:items-start">
      <div className="space-y-5 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Fence run</h2>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <ToolField label="Run length" value={length} onChange={setLength} unit="m" />
            <ToolSelect
              label="Fence type"
              value={fenceType}
              onChange={(v) => setFenceType(v as FenceType)}
              options={[
                { value: 'Palisade', label: 'Palisade' },
                { value: 'Diamond Mesh', label: 'Diamond mesh' },
                { value: 'Timber', label: 'Timber' },
                { value: 'Clearvu', label: 'Clearvu' },
              ]}
            />
          </div>
        </div>
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Prices</h2>
          <div className="mt-3 grid grid-cols-3 gap-3">
            <ToolField label="Per metre" value={pricePerM} onChange={setPricePerM} unit="R/m" />
            <ToolField label="Per post" value={pricePerPost} onChange={setPricePerPost} unit="R" />
            <ToolField label="Concrete" value={concreteBagPrice} onChange={setConcreteBagPrice} unit="R/bag" />
          </div>
        </div>
      </div>

      <ToolResultCard title="Your fencing estimate">
        <ToolResultRow label="Posts needed" value={`${r.posts} posts`} />
        <ToolResultRow label="Panels / sections" value={`${r.panels} panels`} />
        <ToolResultRow label="Concrete bags" value={`${r.concreteBags} bags`} />
        <ToolResultRow label="Estimated material cost" value={toolCurrency(r.cost)} strong />
      </ToolResultCard>
    </div>
  )
}
